/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { GroupMemberPaging, GroupPaging } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class GroupsApi extends BaseApi {
    createGroup(groupBodyCreate, opts) {
        throwIfNotDefined(groupBodyCreate, 'groupBodyCreate');
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/groups',
            queryParams,
            bodyParam: groupBodyCreate
        });
    }
    createGroupMembership(groupId, groupMembershipBodyCreate, opts) {
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(groupMembershipBodyCreate, 'groupMembershipBodyCreate');
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/groups/{groupId}/members',
            pathParams: { groupId },
            queryParams,
            bodyParam: groupMembershipBodyCreate
        });
    }
    deleteGroup(groupId, opts) {
        throwIfNotDefined(groupId, 'groupId');
        opts = opts || {};
        const cascadeDelete = opts['cascade'] ? opts['cascade'] : false;
        const pathParams = {
            groupId
        };
        const queryParams = {
            cascade: cascadeDelete
        };
        return this.delete({
            path: '/groups/{groupId}',
            pathParams,
            queryParams
        });
    }
    deleteGroupMembership(groupId, groupMemberId) {
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(groupMemberId, 'groupMemberId');
        const pathParams = {
            groupId,
            groupMemberId
        };
        return this.delete({
            path: '/groups/{groupId}/members/{groupMemberId}',
            pathParams
        });
    }
    getGroup(groupId, opts) {
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            groupId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/groups/{groupId}',
            pathParams,
            queryParams
        });
    }
    listGroupMemberships(groupId, opts) {
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            groupId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/groups/{groupId}/members',
            pathParams,
            queryParams,
            returnType: GroupMemberPaging
        });
    }
    listGroupMembershipsForPerson(personId, opts) {
        throwIfNotDefined(personId, 'personId');
        const pathParams = {
            personId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/people/{personId}/groups',
            pathParams,
            queryParams,
            returnType: GroupPaging
        });
    }
    listGroups(opts) {
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/groups',
            queryParams,
            returnType: GroupPaging
        });
    }
    updateGroup(groupId, groupBodyUpdate, opts) {
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(groupBodyUpdate, 'groupBodyUpdate');
        const pathParams = {
            groupId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.put({
            path: '/groups/{groupId}',
            pathParams,
            queryParams,
            bodyParam: groupBodyUpdate
        });
    }
}
